import { AddOutlined, EditOutlined, LinkOutlined, MailOutlined, OpenInNewOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Alert, Badge, Chip, Container, Snackbar } from '@mui/material';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import MicrospecDataTable from '../../../../MicrospecDynamicUI/Components/DataTable/MicrospecFirestoreDataTable';
import { useMicrospecDynamicUIContext } from '../../../../MicrospecDynamicUI/GlobalProvider';
// @ts-ignore
import { getDataFromCollection, updateData } from '../../../../MicrospecDynamicUI/Helpers/dataUtils';
import AddClientDialog, { type AddClientSubmitData } from './AddClientDialog';
import AssignClientDialog from './AssignClientDialog';
import MessagesDrawer from './MessagesDrawer';

interface ManagePropertiesProps {
  includeOwnerIdQuery?: boolean;
  enableOwnersActions?: boolean;
}

const ManageProperties = ({ includeOwnerIdQuery = false, enableOwnersActions = false }: ManagePropertiesProps) => {
  const navigate = useNavigate();
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const userProfile = (cloudProvider as any).userProfile?.attributes;
  const firestore = (cloudProvider as any).firestore;
  const [refreshKey, setRefreshKey] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('تم نسخ الرابط بنجاح');

  // ── Messages Drawer State ──────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [unprocessedMessages, setUnprocessedMessages] = useState<any[]>([]);
  const [processedMessages, setProcessedMessages] = useState<any[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // ── Add Client Dialog State ────────────────────────────────────────────
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientDialogData, setClientDialogData] = useState<{ firstName: string; lastName: string; contacts: { type: 'phone' | 'email'; value: string }[] }>({ firstName: '', lastName: '', contacts: [] });
  const [clientSaving, setClientSaving] = useState(false);
  const [clientSourceMsg, setClientSourceMsg] = useState<{ id: string; collection: string } | null>(null);

  // ── Assign Client Dialog State ─────────────────────────────────────────
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [ownerClients, setOwnerClients] = useState<any[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [assignSourceMsg, setAssignSourceMsg] = useState<{ id: string; collection: string } | null>(null);

  const handleOpenAddClient = useCallback((msg: any) => {
    const firstName = msg.senderFirstName || (msg.senderName || '').trim().split(/\s+/)[0] || '';
    const lastName = msg.senderLastName || '';
    const contacts: { type: 'phone' | 'email'; value: string }[] = [];
    if (msg.senderPhone) contacts.push({ type: 'phone', value: msg.senderPhone });
    setClientDialogData({ firstName, lastName, contacts });
    // Track which message and which subcollection it belongs to
    const isUnprocessed = unprocessedMessages.some((m) => m.id === msg.id);
    setClientSourceMsg({ id: msg.id, collection: isUnprocessed ? 'unprocessed_messages' : 'processed_messages' });
    setClientDialogOpen(true);
  }, [unprocessedMessages]);

  const handleOpenAssignClient = useCallback(async (msg: any) => {
    if (!firestore?.instance || !userProfile?.uid) return;
    const isUnprocessed = unprocessedMessages.some((m) => m.id === msg.id);
    setAssignSourceMsg({ id: msg.id, collection: isUnprocessed ? 'unprocessed_messages' : 'processed_messages' });
    setAssignDialogOpen(true);
    setClientsLoading(true);
    try {
      const db = firestore.instance;
      const snap = await getDocs(
        collection(db, 'clients')
      );
      const owned = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c: any) => c.owner_id === userProfile.uid);
      setOwnerClients(owned);
    } catch (err) {
      console.error('Error loading clients:', err);
    } finally {
      setClientsLoading(false);
    }
  }, [firestore?.instance, userProfile?.uid, unprocessedMessages]);

  const handleAssignClient = useCallback(async (client: any) => {
    if (!firestore?.instance || !selectedProperty?.id || !assignSourceMsg) return;
    try {
      const db = firestore.instance;
      const msgRef = doc(db, `properties/${selectedProperty.id}/${assignSourceMsg.collection}`, assignSourceMsg.id);
      await updateDoc(msgRef, { client_id: client.id });

      const enrichMsg = (m: any) =>
        m.id === assignSourceMsg.id ? { ...m, client_id: client.id, _client: client } : m;
      if (assignSourceMsg.collection === 'unprocessed_messages') {
        setUnprocessedMessages((prev) => prev.map(enrichMsg));
      } else {
        setProcessedMessages((prev) => prev.map(enrichMsg));
      }

      setAssignDialogOpen(false);
      setSnackMessage('تم ربط الرسالة بالعميل بنجاح');
      setSnackOpen(true);
    } catch (err) {
      console.error('Error assigning client:', err);
    }
  }, [firestore?.instance, selectedProperty?.id, assignSourceMsg]);

  const handleSubmitClient = useCallback(async (data: AddClientSubmitData) => {
    if (!firestore?.instance || !userProfile?.uid || !selectedProperty?.id) return;
    setClientSaving(true);
    try {
      const db = firestore.instance;
      const clientRef = await addDoc(collection(db, 'clients'), {
        first_name: data.firstName,
        last_name: data.lastName,
        contacts: data.contacts,
        owner_id: userProfile.uid,
        created_at: serverTimestamp(),
      });

      // Update the source message with client_id
      if (clientSourceMsg) {
        const msgRef = doc(db, `properties/${selectedProperty.id}/${clientSourceMsg.collection}`, clientSourceMsg.id);
        await updateDoc(msgRef, { client_id: clientRef.id });

        // Optimistic update: enrich the message in local state with client data
        const clientData = {
          id: clientRef.id,
          first_name: data.firstName,
          last_name: data.lastName,
          contacts: data.contacts,
        };
        const enrichMsg = (m: any) =>
          m.id === clientSourceMsg.id ? { ...m, client_id: clientRef.id, _client: clientData } : m;
        if (clientSourceMsg.collection === 'unprocessed_messages') {
          setUnprocessedMessages((prev) => prev.map(enrichMsg));
        } else {
          setProcessedMessages((prev) => prev.map(enrichMsg));
        }
      }

      setClientDialogOpen(false);
      setSnackMessage('تمت إضافة العميل بنجاح');
      setSnackOpen(true);
    } catch (err) {
      console.error('Error adding client:', err);
    } finally {
      setClientSaving(false);
    }
  }, [firestore?.instance, userProfile?.uid, selectedProperty?.id, clientSourceMsg]);

  const moveMessage = useCallback(async (msgId: string, msgData: any, fromCollection: string, toCollection: string) => {
    try {
      const db = firestore.instance;
      const propertyId = selectedProperty?.id;
      const toRef = doc(db, `properties/${propertyId}/${toCollection}`, msgId);
      const fromRef = doc(db, `properties/${propertyId}/${fromCollection}`, msgId);
      await setDoc(toRef, msgData);
      await deleteDoc(fromRef);
      if (fromCollection === 'unprocessed_messages') {
        setUnprocessedMessages((prev) => prev.filter((m) => m.id !== msgId));
        setProcessedMessages((prev) => [...prev, { id: msgId, ...msgData }]);
      } else {
        setProcessedMessages((prev) => prev.filter((m) => m.id !== msgId));
        setUnprocessedMessages((prev) => [...prev, { id: msgId, ...msgData }]);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Error moving message:', err);
    }
  }, [firestore?.instance, selectedProperty?.id]);

  const openMessagesDrawer = useCallback(async (row: any) => {
    setSelectedProperty(row);
    setDrawerOpen(true);
    setDrawerTab(0);
    setMessagesLoading(true);
    try {
      const db = firestore.instance;
      const [unprocessedSnap, processedSnap] = await Promise.all([
        getDocs(collection(db, `properties/${row.id}/unprocessed_messages`)),
        getDocs(collection(db, `properties/${row.id}/processed_messages`)),
      ]);
      const rawUnprocessed = unprocessedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const rawProcessed = processedSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Enrich messages that have client_id with client data
      const allMsgs = [...rawUnprocessed, ...rawProcessed];
      const clientIds = [...new Set(allMsgs.filter((m: any) => m.client_id).map((m: any) => m.client_id))];
      const clientsMap: Record<string, any> = {};
      await Promise.all(
        clientIds.map(async (cid) => {
          try {
            const snap = await getDoc(doc(db, 'clients', cid));
            if (snap.exists()) clientsMap[cid] = { id: snap.id, ...snap.data() };
          } catch (_) { /* ignore */ }
        })
      );
      const enrich = (m: any) => m.client_id && clientsMap[m.client_id] ? { ...m, _client: clientsMap[m.client_id] } : m;
      setUnprocessedMessages(rawUnprocessed.map(enrich));
      setProcessedMessages(rawProcessed.map(enrich));
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  }, [firestore?.instance]);

  const onDataLoad = useCallback(async (rows: any[]) => {
    if (!firestore?.instance) return rows;
    const db = firestore.instance;

    // Fetch categories
    const catsMap: Record<string, string> = await new Promise((resolve) => {
      getDataFromCollection(
        (cats: any[]) => {
          const map: Record<string, string> = {};
          (cats || []).forEach((c) => { map[c.id] = c.name || c.label; });
          resolve(map);
        },
        () => {},
        db,
        'categories',
        true,
        50
      );
    });

    // Fetch unprocessed message counts per property
    const enriched = await Promise.all(
      rows.map(async (row) => {
        let unprocessedCount = 0;
        try {
          const snap = await getDocs(collection(db, `properties/${row.id}/unprocessed_messages`));
          unprocessedCount = snap.size;
        } catch (_) { /* ignore */ }
        return {
          ...row,
          category_name: catsMap[row.category_id] || row.category_id,
          unprocessed_count: unprocessedCount,
        };
      })
    );

    return enriched;
  }, [firestore?.instance]);

  const wheres = useMemo(
    () => (includeOwnerIdQuery && userProfile?.uid ? [where('owner_id', '==', userProfile.uid)] : []),
    [includeOwnerIdQuery, userProfile?.uid]
  );

  const columns = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'العنوان',
        minWidth: 200,
      },
      {
        field: 'category_name',
        headerName: 'التصنيف',
        minWidth: 120,
      },
      {
        field: 'price',
        headerName: 'السعر',
        minWidth: 120,
        render: (value: any, row: any) =>
          row.price_on_request ? 'عند الطلب' : `₪ ${Number(value || 0).toLocaleString()}`,
      },
      {
        field: 'active',
        headerName: 'الحالة',
        minWidth: 100,
        render: (value: any) => (
          <Chip
            component="span"
            size="small"
            label={value ? 'منشور' : 'مخفي'}
            color={value ? 'success' : 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'approval_status',
        headerName: 'الموافقة',
        minWidth: 140,
        render: (value: any) => {
          const map: Record<string, { label: string; color: any }> = {
            approved: { label: 'مُوافق عليه', color: 'primary' },
            declined: { label: 'مرفوض', color: 'error' },
            under_review: { label: 'تحت المراجعة', color: 'warning' },
          };
          const cfg = map[value] || { label: 'تحت المراجعة', color: 'warning' };
          return (
            <Chip
              component="span"
              size="small"
              label={cfg.label}
              color={cfg.color}
              variant="outlined"
            />
          );
        },
      },
    ],
    []
  );

  const handleToggleActive = useCallback(
    async (row: any) => {
      const newActive = !row.active;
      const msg = newActive ? 'هل تريد نشر هذا العقار؟' : 'هل تريد إلغاء نشر هذا العقار؟';
      if (!window.confirm(msg)) return;
      try {
        await updateData(`properties/${row.id}`, firestore, { active: newActive });
        setRefreshKey((k) => k + 1);
      } catch (err) {
        console.error('Error toggling property:', err);
      }
    },
    [firestore]
  );

  const actions = useMemo(
    () => [
      {
        icon: <OpenInNewOutlined fontSize="small" />,
        label: 'عرض',
        onClick: (row: any) => window.open(`/property/${row.id}`, '_blank'),
      },
      ...(enableOwnersActions ? [
        {
          icon: <EditOutlined fontSize="small" />,
          label: 'تعديل',
          onClick: (row: any) => navigate(`/owners/properties/edit/${row.id}`),
        },
        {
          icon: <LinkOutlined fontSize="small" />,
          label: 'نسخ الرابط',
          onClick: (row: any) => {
            navigator.clipboard.writeText(`${window.location.origin}/property/${row.id}`);
            setSnackMessage('تم نسخ الرابط بنجاح');
            setSnackOpen(true);
          },
        },
        {
          icon: (row: any) => (
            <Badge badgeContent={row.unprocessed_count || 0} color="error" showZero={false}>
              <MailOutlined fontSize="small" />
            </Badge>
          ),
          label: 'الرسائل',
          onClick: openMessagesDrawer,
        },
        {
          icon: (row: any) => row.active ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />,
          label: (row: any) => row.active ? 'إلغاء النشر' : 'نشر',
          onClick: handleToggleActive,
        },
      ] : []),
    ],
    [enableOwnersActions, navigate, handleToggleActive, openMessagesDrawer]
  );

  const globalActions = useMemo(
    () => enableOwnersActions ? [
      {
        icon: <AddOutlined />,
        label: 'إضافة عقار',
        onClick: () => navigate('/owners/properties/add'),
        variant: 'contained' as const,
        color: 'primary' as const,
      },
    ] : [],
    [enableOwnersActions, navigate]
  );

  if (includeOwnerIdQuery && !userProfile?.uid) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <MicrospecDataTable
        key={refreshKey}
        collection="properties"
        columns={columns}
        wheres={wheres}
        onDataLoad={onDataLoad}
        actions={actions}
        globalActions={globalActions}
        title={{ type: 'Literal', text: 'عقاراتي' } as any}
        subtitle={{ type: 'Literal', text: 'إدارة جميع العقارات الخاصة بك' } as any}
        searchable
        refreshable
        emptyMessage="لا توجد عقارات حتى الآن"
        pageSize={10}
        onRowClick={(row: any) => enableOwnersActions && navigate(`/owners/properties/edit/${row.id}`)}
      />
      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ position: 'fixed', bottom: 24 }}
      >
        <Alert severity="success" variant="filled">
          {snackMessage}
        </Alert>
      </Snackbar>

      <MessagesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        propertyTitle={selectedProperty?.title}
        unprocessedMessages={unprocessedMessages}
        processedMessages={processedMessages}
        messagesLoading={messagesLoading}
        tab={drawerTab}
        onTabChange={setDrawerTab}
        onMoveMessage={moveMessage}
        onAddClient={handleOpenAddClient}
        onAssignClient={handleOpenAssignClient}
      />

      <AddClientDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSubmit={handleSubmitClient}
        initialFirstName={clientDialogData.firstName}
        initialLastName={clientDialogData.lastName}
        initialContacts={clientDialogData.contacts}
        loading={clientSaving}
      />

      <AssignClientDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onSelect={handleAssignClient}
        clients={ownerClients}
        loading={clientsLoading}
      />
    </Container>
  );
};

export default ManageProperties;
